import { SessionData, SubscriptionData, UserData } from "@interfaces/auth";
import toast from "react-hot-toast";
import { auth } from "@services/auth/auth-service";

export const setSession = async (
  set: any,
  get: any,
  data: { user: UserData; session: SessionData },
) => {
  set({
    isAuthenticated: data.user ? true : false,
    user: data.user,
    session: data.session,
  });
};

export const removeSession = async (set: any, get: any) => {
  set({
    isAuthenticated: false,
    user: null,
    session: null,
  });
};

export const replaceSession = async (
  set: any,
  get: any,
  session: SessionData,
) => {
  set({
    session,
  });
};

export const expireSession = async (set: any, get: any) => {
  // before expiring the session, we need to ask the backend to make sure
  const response = await auth.verifySession();
  if (response.data && response.data.session) return;

  // performing the expiration
  toast.error("Your session has expired. Please login again.");

  set({
    isAuthenticated: false,
    user: null,
    session: null,
  });
};

export const setSubscription = async (
  set: any,
  get: any,
  subscription: SubscriptionData,
) => {
  set((state: AuthState) => ({
    ...state,
    user: {
      ...state.user,
      profile: {
        ...state.user.profile,
        subscriptions: subscription,
      },
    },
  }));
};

export const enableWalkthrough = async (
  set: any,
  get: any,
  enable: boolean,
) => {
  set((state: AuthState) => ({
    ...state,
    user: {
      ...state.user,
      profile: {
        ...state.user.profile,
        skip_onboarding: !enable,
      },
    },
  }));
};

export interface AuthState {
  isAuthenticated: boolean;
  user: UserData;
  session: SessionData | null;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setUser: (user: UserData | null) => void;
  setSubscription: (subscription: SubscriptionData) => void;
  enableWalkthrough: (enable: boolean) => void;
  setSession: (user: UserData | null, session: SessionData | null) => void;
  removeSession: () => void;
  replaceSession: (session: SessionData) => void;
  expireSession: () => void;
}

export const createAuthSlice = (
  set: (fn: (state: AuthState) => AuthState) => void,
  get: () => AuthState,
) => ({
  isAuthenticated: false,
  user: null,
  session: null,
  setIsAuthenticated: (isAuthenticated: boolean) =>
    set((state: AuthState) => ({ ...state, isAuthenticated })),
  setUser: (user: UserData) => set((state: AuthState) => ({ ...state, user })),
  setSubscription: (subscription: SubscriptionData) =>
    setSubscription(set, get, subscription),
  enableWalkthrough: (enable: boolean) => enableWalkthrough(set, get, enable),
  setSession: (user: UserData, session: SessionData) =>
    setSession(set, get, { user, session }),
  removeSession: () => removeSession(set, get),
  replaceSession: (session: SessionData) => replaceSession(set, get, session),
  expireSession: () => expireSession(set, get),
});
